/**
 * Configure Release Dialog
 *
 * Dialog for configuring Release QA settings for a property.
 * Allows selecting GitHub repos, choosing regression scope,
 * and configuring automated scheduling.
 */

import React from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import {
  DialogTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  Button,
  Select,
  TextField,
  TextArea,
  NumberField,
  Text,
  Flex,
  Box,
  LoadingOrbit,
  useToast,
  CollapsibleSection,
  Switch,
  Banner,
  MultiSelectComboBox,
  type MultiSelectOption,
} from '@chatsdet/ui';
import {
  Warning,
  GitBranch,
  Plus,
  X,
  GithubLogo,
  Tag as TagIcon,
  Sliders,
  Clock,
  TrashSimple,
  Robot,
  BellRinging,
} from '@phosphor-icons/react';
import { useEligibilityModel } from '../../../hooks/useEligibilityModel';
import { useTestSuites } from '../../test-suites/useTestSuites';
import {
  usePropertyGitHubRepos,
  useSaveReleaseConfig,
  useDeleteReleaseConfig,
} from '../useReleases';
import { AlertDestinationEditor } from '../../test-suites/AlertDestinationEditor';
import type { SuiteAlertConfig } from '../../test-suites/types';
import { useSystemAlertRule, useSaveSystemAlertRule } from '../../scheduling/useSystemAlertRule';
import { ReleaseScheduleSettings } from './ReleaseScheduleSettings';
import { resolveSchedulePayload } from '../releaseSchedulePayload';
import { ApiError } from '../../../lib/api-error';
import type {
  ReleaseQAConfig,
  ReleaseQAConfigRepoEntry,
  ReleaseQAConfigSuiteEntry,
  ScheduleSettings,
} from '../releaseTypes';

type ConfigureReleaseDialogProps = {
  propertyId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  existingConfig?: ReleaseQAConfig | null;
  /**
   * Existing repo links from the multi-repo junction table. When supplied the
   * dialog hydrates the repeatable repo list from this; otherwise it falls
   * back to the legacy single-repo column on `existingConfig`.
   */
  existingRepos?: ReleaseQAConfigRepoEntry[];
  /** Suites currently linked to the release agent for this config. */
  existingSuites?: ReleaseQAConfigSuiteEntry[];
};

/** Form-side representation of one linked repo. */
type RepoRow = {
  propertyGithubRepoId: string;
  releaseBranch: string;
  role: string;
};

type FormState = {
  repos: RepoRow[];
  regressionEnvironmentId: string | null;
  lookbackDays: number;
  schedule: ScheduleSettings | null;
  agentPromptMd: string;
  agentMayPromote: boolean;
  suiteIds: string[];
};

const EMPTY_REPO_ROW: RepoRow = {
  propertyGithubRepoId: '',
  releaseBranch: '',
  role: '',
};

const DEFAULT_FORM_STATE: FormState = {
  repos: [{ ...EMPTY_REPO_ROW }],
  regressionEnvironmentId: null,
  lookbackDays: 7,
  schedule: null,
  agentPromptMd: '',
  agentMayPromote: false,
  suiteIds: [],
};

export const ConfigureReleaseDialog: React.FC<ConfigureReleaseDialogProps> = ({
  propertyId,
  isOpen,
  onOpenChange,
  existingConfig,
  existingRepos,
  existingSuites,
}) => {
  const { toast } = useToast();
  // "Run all" targets promoted flows on the legacy model (flag OFF), eligible flows on the
  // eligibility model (flag ON) — keep the copy accurate for the org's actual model.
  const runnableWord = useEligibilityModel() ? 'eligible' : 'promoted';
  const githubReposQuery = usePropertyGitHubRepos(propertyId);
  const saveMutation = useSaveReleaseConfig();
  const deleteMutation = useDeleteReleaseConfig();
  const testSuitesQuery = useTestSuites(isOpen);

  const releaseAlertsQuery = useSystemAlertRule('release_qa.completed', isOpen);
  const saveAlertsMutation = useSaveSystemAlertRule('release_qa.completed', 'Release alerts');
  const [alerts, setAlerts] = React.useState<SuiteAlertConfig>({ steps: [] });
  // Alerts as loaded from the server; only a real edit is sent on save.
  const alertsBaselineRef = React.useRef<SuiteAlertConfig>({ steps: [] });
  // True once `alerts` reflects the server for this open; gates the alerts section.
  const [alertsInitialized, setAlertsInitialized] = React.useState(false);

  const [form, setForm] = React.useState<FormState>(DEFAULT_FORM_STATE);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  // Inline error rendered above the footer, for server-side validation
  // failures from the release agent's typed error catalog (RELEASE_AGENT_*).
  const [saveError, setSaveError] = React.useState<string | null>(null);
  // The schedule as loaded from the server, so the submit handler can tell an
  // untouched schedule (send nothing → leave it alone) from a real edit.
  const scheduleBaselineRef = React.useRef<ScheduleSettings | null>(null);

  // Initialize form when dialog opens or config changes
  React.useEffect(() => {
    if (isOpen) {
      if (existingConfig) {
        // Repos come from the multi-repo junction list returned by the API.
        const initialRepos: RepoRow[] =
          existingRepos && existingRepos.length > 0
            ? existingRepos.map((r) => ({
                propertyGithubRepoId: r.propertyGithubRepoId,
                releaseBranch: r.releaseBranch ?? '',
                role: r.role ?? '',
              }))
            : [{ ...EMPTY_REPO_ROW }];
        const initialSchedule = existingConfig.schedule ?? null;
        scheduleBaselineRef.current = initialSchedule;
        setForm({
          repos: initialRepos,
          regressionEnvironmentId: existingConfig.regressionEnvironmentId ?? null,
          lookbackDays: existingConfig.lookbackDays,
          schedule: initialSchedule,
          agentPromptMd: existingConfig.agentPromptMd ?? '',
          agentMayPromote: existingConfig.agentMayPromote ?? false,
          suiteIds: (existingSuites ?? []).map((s) => s.testSuiteId),
        });
      } else {
        scheduleBaselineRef.current = null;
        setForm({ ...DEFAULT_FORM_STATE, repos: [{ ...EMPTY_REPO_ROW }] });
      }
      setSaveError(null);
      setDeleteConfirmOpen(false);
    }
  }, [isOpen, existingConfig, existingRepos, existingSuites]);

  // Initialize alerts once per open, when the query first has an answer (a rule or
  // null). Later refetches must not clobber in-progress edits.
  React.useEffect(() => {
    if (!isOpen) {
      setAlertsInitialized(false);
      return;
    }
    if (alertsInitialized || !releaseAlertsQuery.isSuccess) return;
    const loaded = releaseAlertsQuery.data?.alertConfig ?? { steps: [] };
    alertsBaselineRef.current = loaded;
    setAlerts(loaded);
    setAlertsInitialized(true);
  }, [isOpen, alertsInitialized, releaseAlertsQuery.isSuccess, releaseAlertsQuery.data]);

  const linkedRepos = githubReposQuery.data?.items ?? [];

  const environmentsQuery = useQuery({
    queryKey: ['property-environments', propertyId],
    enabled: isOpen,
    queryFn: async (): Promise<{
      items: Array<{ id: string; name: string; isDefault: boolean }>;
    }> => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/environments`,
        { credentials: 'include' }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to fetch environments');
      return r.json();
    },
  });
  const environmentItems = React.useMemo(
    () => [
      { id: '__none__', label: 'Any (no preference)' },
      ...(environmentsQuery.data?.items ?? []).map((e) => ({
        id: e.id,
        label: e.isDefault ? `${e.name} (default)` : e.name,
      })),
    ],
    [environmentsQuery.data?.items]
  );

  // When envs load for a brand-new config (no saved preference) default the
  // picker to the property's default environment. Editing an existing config
  // preserves whatever was saved (including null = "Any").
  React.useEffect(() => {
    if (!environmentsQuery.data || existingConfig) return;
    if (form.regressionEnvironmentId !== null) return;
    const defaultEnv = environmentsQuery.data.items.find((e) => e.isDefault);
    if (defaultEnv) {
      setForm((f) => ({ ...f, regressionEnvironmentId: defaultEnv.id }));
    }
  }, [environmentsQuery.data, existingConfig, form.regressionEnvironmentId]);

  const updateRepo = (idx: number, patch: Partial<RepoRow>) => {
    setForm((f) => ({
      ...f,
      repos: f.repos.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };
  const addRepoRow = () => {
    setForm((f) => ({ ...f, repos: [...f.repos, { ...EMPTY_REPO_ROW }] }));
  };
  const removeRepoRow = (idx: number) => {
    setForm((f) => ({
      ...f,
      repos: f.repos.length === 1 ? f.repos : f.repos.filter((_, i) => i !== idx),
    }));
  };

  const selectedRepoIds = new Set(
    form.repos.map((r) => r.propertyGithubRepoId).filter(Boolean)
  );

  const handleSave = async () => {
    setSaveError(null);
    const validRepos = form.repos.filter((r) => r.propertyGithubRepoId);
    if (validRepos.length === 0) {
      toast.error('Please select at least one GitHub repository');
      return;
    }
    // Reject duplicates within the form before round-tripping to the server.
    const seen = new Set<string>();
    for (const r of validRepos) {
      if (seen.has(r.propertyGithubRepoId)) {
        toast.error('Each repository can only be added once');
        return;
      }
      seen.add(r.propertyGithubRepoId);
    }
    if (form.suiteIds.length === 0) {
      toast.error('Please select at least one regression suite');
      return;
    }

    // Map the form's schedule to the backend's tri-state (undefined = unchanged,
    // null = remove, object = upsert) so we never clobber a schedule the user
    // didn't touch. See resolveSchedulePayload for the rules.
    const schedulePayload = resolveSchedulePayload({
      isNew: !existingConfig,
      formSchedule: form.schedule,
      baselineSchedule: scheduleBaselineRef.current,
    });

    try {
      await saveMutation.mutateAsync({
        propertyId,
        isNew: !existingConfig,
        repos: validRepos.map((r) => ({
          propertyGithubRepoId: r.propertyGithubRepoId,
          releaseBranch: r.releaseBranch.trim() || null,
          role: r.role.trim() || null,
        })),
        // The persisted suite selection is the regression selector for every
        // config, agent or not — see `docs/features/release-qa.md`.
        suiteIds: form.suiteIds,
        regressionEnvironmentId: form.regressionEnvironmentId,
        lookbackDays: form.lookbackDays,
        schedule: schedulePayload,
        // The release agent (create route doesn't accept these fields — the
        // Agent section only renders once a config exists, see below).
        ...(existingConfig
          ? {
              agentPromptMd: form.agentPromptMd.trim() || null,
              agentMayPromote: form.agentMayPromote,
            }
          : {}),
      });

      const alertsChanged =
        JSON.stringify(alerts) !== JSON.stringify(alertsBaselineRef.current);
      if (alertsChanged && alertsInitialized) {
        const ruleId = releaseAlertsQuery.data?.id ?? null;
        // No rule yet and nothing to send: nothing to create.
        if (ruleId || alerts.steps.length > 0) {
          try {
            await saveAlertsMutation.mutateAsync({ ruleId, alertConfig: alerts });
          } catch (alertsError) {
            const detail = alertsError instanceof Error ? alertsError.message : 'Unknown error';
            toast.error(`Configuration saved, but release alerts could not be saved: ${detail}`);
            return;
          }
        }
      }

      toast.success(existingConfig ? 'Configuration updated' : 'Configuration created');
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save configuration';
      // Release QA validation errors (RELEASE_QA_*) render inline next to the
      // controls they concern, in addition to the toast, since the relevant
      // section is easy to miss otherwise.
      if (error instanceof ApiError && error.code?.startsWith('RELEASE_QA_')) {
        setSaveError(message);
      }
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(propertyId);
      toast.success('Configuration deleted');
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete configuration');
    }
  };

  const buildRepoOptions = (currentRepoId: string) =>
    linkedRepos
      // Filter out repos already picked in another row, but keep this row's own selection.
      .filter((repo) => repo.id === currentRepoId || !selectedRepoIds.has(repo.id))
      .map((repo) => ({ id: repo.id, label: repo.repoFullName }));

  const isLoading = saveMutation.isPending || saveAlertsMutation.isPending || deleteMutation.isPending;
  const hasAtLeastOneRepo = form.repos.some((r) => r.propertyGithubRepoId);
  const validRepoCount = form.repos.filter((r) => r.propertyGithubRepoId).length;
  // Client-side pre-check mirroring the server's RELEASE_QA_REQUIRES_SUITE
  // error — the server is still the source of truth for this and every other
  // config validation (flag, Slack rule, cross-property suite).
  const canSave = hasAtLeastOneRepo && form.suiteIds.length > 0 && !isLoading;

  const suiteOptions: MultiSelectOption[] = React.useMemo(
    () => (testSuitesQuery.data ?? []).map((suite) => ({ id: suite.id, label: suite.name })),
    [testSuitesQuery.data]
  );
  // Fall back to the config's already-linked suite names (from the GET
  // response) for chips whose suite hasn't loaded into `suiteOptions` yet.
  const selectedSuiteOptions: MultiSelectOption[] = React.useMemo(() => {
    const byId = new Map(suiteOptions.map((opt) => [opt.id, opt] as const));
    const existingById = new Map((existingSuites ?? []).map((s) => [s.testSuiteId, s.name] as const));
    return form.suiteIds.map(
      (id): MultiSelectOption => byId.get(id) ?? { id, label: existingById.get(id) ?? id }
    );
  }, [suiteOptions, form.suiteIds, existingSuites]);

  // Section icons + classes shared by every CollapsibleSection in the dialog.
  // tailwind-merge promotes these classes over CollapsibleSection's defaults.
  const sectionClasses = 'first:border-t-0 first:pt-0';

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
      <span style={{ display: 'none' }} />
      <Dialog
        title="Configure Release QA"
        description="Set up automated testing for code releases"
      >
        <DialogContent>
          {githubReposQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-12">
              <LoadingOrbit size="md" />
            </Flex>
          ) : (
            <Flex direction="column" className="gap-1">
              {/* ─── Repositories ─────────────────────────────────────── */}
              <CollapsibleSection
                label="Repositories"
                icon={<GithubLogo size={14} weight="duotone" className="text-muted-foreground" />}
                count={validRepoCount}
                defaultExpanded
                className={sectionClasses}
              >
                {linkedRepos.length === 0 ? (
                  <Box className="p-4 bg-warning/10 border border-warning/30 rounded-lg">
                    <Flex align="start" gap="sm">
                      <Warning size={20} className="text-warning shrink-0 mt-0.5" />
                      <Box>
                        <Text weight="medium">No repositories linked</Text>
                        <Text size="sm" className="text-muted-foreground mt-1">
                          Link a GitHub repository to this application first.{' '}
                          <Link
                            to="/applications/$propertyId"
                            params={{ propertyId }}
                            search={{ tab: 'code' }}
                            className="text-primary hover:underline"
                          >
                            Go to Code tab
                          </Link>
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                ) : (
                  <Flex direction="column" gap="sm">
                    <Flex direction="column" className="divide-y divide-border">
                    {form.repos.map((row, idx) => {
                      const selectedRepo = linkedRepos.find(
                        (r) => r.id === row.propertyGithubRepoId
                      );
                      const repoOptions = buildRepoOptions(row.propertyGithubRepoId);
                      const isPrimary = idx === 0;
                      return (
                        <Box key={idx} className="py-3 first:pt-0">
                          <Flex align="center" justify="between" className="mb-2">
                            <Flex align="center" gap="xs">
                              <Text
                                size="xs"
                                weight="medium"
                                className="uppercase tracking-wide text-muted-foreground"
                              >
                                {isPrimary ? 'Primary' : `Repo ${idx + 1}`}
                              </Text>
                              {isPrimary && (
                                <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                                  default branch
                                </span>
                              )}
                            </Flex>
                            {form.repos.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onPress={() => removeRepoRow(idx)}
                                aria-label={`Remove repository ${idx + 1}`}
                                className="w-6 p-0"
                              >
                                <X size={14} />
                              </Button>
                            )}
                          </Flex>

                          <Flex direction="column" gap="sm">
                            <Select
                              label="GitHub Repository"
                              placeholder="Select a repository"
                              items={repoOptions}
                              selectedKey={row.propertyGithubRepoId || undefined}
                              onSelectionChange={(key) =>
                                updateRepo(idx, { propertyGithubRepoId: (key as string) ?? '' })
                              }
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <Box>
                                <TextField
                                  label="Release Branch"
                                  value={row.releaseBranch}
                                  onChange={(v) => updateRepo(idx, { releaseBranch: v })}
                                  placeholder={selectedRepo?.defaultBranch ?? 'main'}
                                />
                                <Flex align="center" gap="xs" className="mt-1">
                                  <GitBranch size={11} className="text-muted-foreground" />
                                  <Text size="xs" className="text-muted-foreground leading-tight">
                                    {selectedRepo?.defaultBranch
                                      ? `Default: ${selectedRepo.defaultBranch}`
                                      : "Defaults to repo's default branch"}
                                  </Text>
                                </Flex>
                              </Box>
                              <Box>
                                <TextField
                                  label="Role"
                                  value={row.role}
                                  onChange={(v) => updateRepo(idx, { role: v })}
                                  placeholder="frontend, backend…"
                                />
                                <Text size="xs" className="text-muted-foreground mt-1 leading-tight">
                                  Optional label for the timeline
                                </Text>
                              </Box>
                            </div>
                          </Flex>
                        </Box>
                      );
                    })}
                    </Flex>

                    <Button
                      variant="ghost"
                      size="sm"
                      onPress={addRepoRow}
                      isDisabled={form.repos.length >= linkedRepos.length}
                      className="self-start"
                      iconLeft={<Plus size={14} weight="bold" />}
                    >
                      Add repository
                    </Button>
                  </Flex>
                )}
              </CollapsibleSection>

              {/* ─── Regression suites ────────────────────────────────── */}
              <CollapsibleSection
                label="Regression suites"
                icon={<TagIcon size={14} weight="duotone" className="text-muted-foreground" />}
                count={form.suiteIds.length || undefined}
                defaultExpanded
                className={sectionClasses}
              >
                <Flex direction="column" gap="md">
                  <MultiSelectComboBox
                    label="Suites"
                    placeholder={
                      testSuitesQuery.isLoading ? 'Loading suites...' : 'Search suites...'
                    }
                    items={suiteOptions}
                    value={selectedSuiteOptions}
                    onChange={(next) =>
                      setForm((f) => ({ ...f, suiteIds: next.map((o) => o.id) }))
                    }
                    isLoading={testSuitesQuery.isLoading}
                  />
                  <Text size="xs" className="text-muted-foreground leading-snug">
                    Every release runs these suites.
                  </Text>
                  {existingConfig && form.suiteIds.length === 0 && (
                    <Text size="xs" className="text-destructive leading-snug">
                      This configuration still runs all {runnableWord} workflows — choose at
                      least one suite to save.
                    </Text>
                  )}
                  <Select
                    label="Environment"
                    items={environmentItems}
                    selectedKey={form.regressionEnvironmentId ?? '__none__'}
                    onSelectionChange={(key) => {
                      const next = key && String(key) !== '__none__' ? String(key) : null;
                      setForm((f) => ({ ...f, regressionEnvironmentId: next }));
                    }}
                    isDisabled={environmentsQuery.isLoading}
                  />
                  <Text size="xs" className="text-muted-foreground leading-snug">
                    Credentials resolve against this environment at run time. Leave on
                    "Any" to use each credential's default binding.
                  </Text>
                </Flex>
              </CollapsibleSection>

              {/* ─── Run Settings ─────────────────────────────────────── */}
              <CollapsibleSection
                label="Run Settings"
                icon={<Sliders size={14} weight="duotone" className="text-muted-foreground" />}
                defaultExpanded
                className={sectionClasses}
              >
                <NumberField
                  label="Lookback Days"
                  value={form.lookbackDays}
                  onChange={(v) => setForm((f) => ({ ...f, lookbackDays: v ?? 7 }))}
                  minValue={1}
                  maxValue={90}
                  description="How far back to look for code changes"
                />
              </CollapsibleSection>

              {/* ─── Schedule ─────────────────────────────────────────── */}
              <CollapsibleSection
                label="Schedule"
                icon={<Clock size={14} weight="duotone" className="text-muted-foreground" />}
                defaultExpanded={!!form.schedule?.enabled}
                className={sectionClasses}
              >
                <ReleaseScheduleSettings
                  value={form.schedule}
                  onChange={(schedule) => setForm((f) => ({ ...f, schedule }))}
                />
              </CollapsibleSection>

              {/* ─── Release Agent (edit-only) ─────────────────────────── */}
              {existingConfig && (
                <CollapsibleSection
                  label="Agent"
                  icon={<Robot size={14} weight="duotone" className="text-muted-foreground" />}
                  defaultExpanded={Boolean(form.agentPromptMd) || form.agentMayPromote}
                  className={sectionClasses}
                >
                  <Flex direction="column" gap="md">
                    <Text size="xs" className="text-muted-foreground leading-snug">
                      Every release run is overseen by a durable AI agent. It runs your
                      regression suites, reviews the diff, writes the release notes, asks here
                      or in Slack when it needs a human call, and can promote to production on
                      its own if you allow it.
                    </Text>

                    <TextArea
                      label="Operator instructions"
                      value={form.agentPromptMd}
                      onChange={(v) => setForm((f) => ({ ...f, agentPromptMd: v }))}
                      rows={10}
                      className="font-mono text-xs"
                      placeholder="Tell the agent what matters in this release, which docs or guides in the repo to read, and which monitors or dashboards to watch. Freeform — the agent reads this at the start of every pass."
                    />

                    <Switch
                      isSelected={form.agentMayPromote}
                      onChange={(selected) =>
                        setForm((f) => ({ ...f, agentMayPromote: selected }))
                      }
                      label="May promote to production on its own"
                    />
                    <Text size="xs" className="text-muted-foreground leading-snug -mt-2">
                      Off by default — the agent still asks a human before promoting unless
                      this is on.
                    </Text>

                    <Box className="pt-3 border-t border-border">
                      <Text size="xs" className="text-muted-foreground leading-snug">
                        Where the agent asks questions and reports its verdict is set under
                        Release alerts below.
                      </Text>
                    </Box>
                  </Flex>
                </CollapsibleSection>
              )}

              {/* ─── Release alerts (org-wide) ────────────────────────── */}
              {alertsInitialized && (
              <CollapsibleSection
                label="Release alerts"
                icon={<BellRinging size={14} weight="duotone" className="text-muted-foreground" />}
                defaultExpanded={alerts.steps.length > 0}
                className={sectionClasses}
              >
                <AlertDestinationEditor
                  title="Destinations"
                  description="Alerts apply to every application in this organization. The first Slack channel is where the release agent opens its thread for each release; without one, the agent's questions and results appear only in the app."
                  alertConfig={alerts}
                  isPending={saveAlertsMutation.isPending}
                  onSave={async (next) => setAlerts(next)}
                />
              </CollapsibleSection>
              )}

              {/* ─── Danger Zone (edit-only) ──────────────────────────── */}
              {existingConfig && (
                <CollapsibleSection
                  label="Danger Zone"
                  icon={<TrashSimple size={14} weight="duotone" className="text-destructive" />}
                  className={sectionClasses}
                >
                  <Box className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                    <Flex align="center" justify="between" gap="md">
                      <Box className="min-w-0">
                        <Text size="sm" weight="medium">
                          Delete this configuration
                        </Text>
                        <Text size="xs" className="text-muted-foreground mt-0.5">
                          Removes Release QA from this property and cancels any scheduled runs.
                        </Text>
                      </Box>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-destructive/40 text-destructive hover:bg-destructive/10"
                        onPress={() => setDeleteConfirmOpen(true)}
                      >
                        Delete
                      </Button>
                    </Flex>
                    <DialogTrigger
                      isOpen={deleteConfirmOpen}
                      onOpenChange={setDeleteConfirmOpen}
                    >
                      <span style={{ display: 'none' }} />
                      <Dialog title="Delete Configuration">
                        <Text>
                          Are you sure you want to delete this configuration? This will also
                          remove any scheduled runs.
                        </Text>
                        <DialogFooter>
                          <Flex gap="sm">
                            <Button
                              variant="outline"
                              onPress={() => setDeleteConfirmOpen(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              onPress={() => void handleDelete()}
                              isDisabled={deleteMutation.isPending}
                            >
                              {deleteMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
                            </Button>
                          </Flex>
                        </DialogFooter>
                      </Dialog>
                    </DialogTrigger>
                  </Box>
                </CollapsibleSection>
              )}
            </Flex>
          )}
        </DialogContent>
        {saveError && (
          <Box className="px-1">
            <Banner variant="error">
              <Text size="sm">{saveError}</Text>
            </Banner>
          </Box>
        )}
        <DialogFooter>
          <Button variant="ghost" onPress={() => onOpenChange(false)} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" onPress={() => void handleSave()} isDisabled={!canSave}>
            {saveMutation.isPending
              ? 'Saving...'
              : existingConfig
                ? 'Save Changes'
                : 'Create Configuration'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
};
